import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Chat, defaultChat } from '../types/chat';
import { Message } from '../types/message';
import { AssistantService } from './assistant/assistant.user.service';
import { ClientEvents, ServerEvents, WebSocketService } from './web-socket.service';
import { Assistant } from '../types/assistant';
import { BaseSingletonService } from './base.service';
import { InsightFocusService } from './insight-focus.service';
import { InsightType } from '../types/insights';
import { Business } from './business/business.types';
import { BusinessService } from './business/business.user.service';

@Injectable({
  providedIn: 'root'
})
export class ChatService extends BaseSingletonService implements OnDestroy {
  // State observables
  private waitingForResponse = new BehaviorSubject<boolean>(false);
  waitingForResponse$ = this.waitingForResponse.asObservable();

  private activeChats = new BehaviorSubject<Chat[]>([]);
  activeChats$ = this.activeChats.asObservable();

  private currentChat = new BehaviorSubject<Chat>(defaultChat);
  currentChat$ = this.currentChat.asObservable();

  private waitingForRating = new BehaviorSubject<boolean>(false);
  waitingForRating$ = this.waitingForRating.asObservable();

  chatAvailable = new BehaviorSubject<boolean>(false);
  chatAvailable$ = this.chatAvailable.asObservable();

  private startOfThread = new BehaviorSubject<boolean>(false);
  startOfThread$ = this.startOfThread.asObservable();

  focusedBusiness: Business | null = null;
  private messageLimit = 20;
  private isFetchingMessages = false;

  constructor(
    private assistantService: AssistantService,
    private webSocketService: WebSocketService,
    private insightFocusService: InsightFocusService,
    private businessService: BusinessService
  ) {
    super();
    this.webSocketService.connect();
    this.initializeSocketEvents();
    this.subscribeToBusinessChanges();
    this.subscribeToInsightChanges();
  }

  // ----- Subscriptions -----

  /**
   * Subscribes to changes in the focused business.
   */
  private subscribeToBusinessChanges(): void {
    this.subscribe(this.businessService.focusedBusiness$, async (business) => {
      this.focusedBusiness = business;
      await this.refreshChat();
    });
  }

  /**
   * Subscribes to changes in the focused insight model.
   */
  private subscribeToInsightChanges(): void {
    this.subscribe(this.insightFocusService.focusedInsightModelChange, async (insightType: string | null) => {
      if (insightType) {
        try {
          const current = this.currentChat.value;
          if (current?.assistant) {
            current.assistant.insightType = insightType as InsightType;
          }
          // Emit using the non-null insightType and business id (if available)
          this.webSocketService.emit(ClientEvents.init, {
            insightType,
            businessId: this.focusedBusiness?._id
          });
          this.chatAvailable.next(true);
        } catch (error) {
          console.error('Error setting insight type:', error);
        }
      } else {
        // Reset chat if no insight is selected
        this.chatAvailable.next(false);
        this.currentChat.next(defaultChat);
        this.activeChats.next([]);
        this.waitingForResponse.next(false);
      }
      await this.refreshChat();
    });
  }

  /**
   * Initializes socket events for handling server responses.
   */
  private initializeSocketEvents(): void {
    // Update assistant name on init event
    this.subscribe(this.webSocketService.on([ServerEvents.init]), ({ assistantName }) => {
      const current = this.currentChat.value;
      if (current?.assistant) {
        current.assistant.name = assistantName;
      }
    });

    // Process bot responses
    this.subscribe(this.webSocketService.on([ServerEvents.response]), ({ message, choices }) => {
      console.log('Received response event:', { message, choices });
      // Handle case where the server sends a JSON string instead of parsed objects
      let messageText = message;
      let messageChoices = choices;
      
      if (typeof message === 'string') {
        try {
          // Try parsing the message as JSON
          const parsedMessage = JSON.parse(message);
          if (parsedMessage) {
            if (typeof parsedMessage.message === 'string') {
              messageText = parsedMessage.message;
            }
            if (Array.isArray(parsedMessage.choices)) {
              messageChoices = parsedMessage.choices;
            }
          }
        } catch {
          // If not valid JSON, use the string as-is
        }
      }
      
      this.addMessageToCurrentChat({
        role: 'bot',
        message: messageText,
        choices: messageChoices,
        timestamp: new Date()
      });
      this.waitingForResponse.next(false);
    });

    // Process final results and update insight service
    this.subscribe(this.webSocketService.on([ServerEvents.result]), ({ message: result }) => {
      const { insightType } = this.currentChat.value.assistant;
      this.insightFocusService.setUpdatedFocusedInsight({ insightType, result });
      console.log('Result received:', result);
      this.waitingForResponse.next(false);
      this.waitingForRating.next(true);
    });
  }

  // ----- Chat Initialization & Updates -----

  /**
   * Initializes a new chat with the provided assistant using the current insight and business.
   */
  private async initializeChat(assistant: Assistant): Promise<void> {
    const focusedInsight = this.insightFocusService.getFocusedInsightModel();
    if (!focusedInsight || !this.focusedBusiness) {
      console.error('Missing focused insight or business at init');
      return;
    }
    try {
      const chatHistory = await this.assistantService.getAssistantHistory(
        focusedInsight,
        this.focusedBusiness._id,
        this.messageLimit
      );
      const newChat: Chat = { assistant, messages: chatHistory };
      this.activeChats.next([...this.activeChats.value, newChat]);
      this.currentChat.next(newChat);
    } catch (error) {
      console.error('Error initializing chat:', error);
    }
  }

  /**
   * Adds a new chat with the provided assistant.
   */
  async addChat(assistant: Assistant): Promise<void> {
    await this.initializeChat(assistant);
  }

  /**
   * Refreshes the current chat or resets state if no business or insight is selected.
   */
  async refreshChat(): Promise<void> {
    if (this.focusedBusiness && this.currentChat.value.assistant?.insightType) {
      await this.initializeChat(this.currentChat.value.assistant);
    } else {
      this.resetChatState();
    }
  }

  /**
   * Resets the chat state to default.
   */
  private resetChatState(): void {
    this.currentChat.next(defaultChat);
    this.chatAvailable.next(false);
    this.activeChats.next([]);
  }

  /**
   * Loads initial messages for the provided assistant.
   */
  async loadInitialMessages(assistant: Assistant): Promise<void> {
    const focusedInsight = this.insightFocusService.getFocusedInsightModel();
    if (!focusedInsight || !this.focusedBusiness) {
      console.error('Missing focused insight or business at load');
      return;
    }
    try {
      const chatHistory = await this.assistantService.getAssistantHistory(
        focusedInsight,
        this.focusedBusiness._id,
        this.messageLimit
      );
      const newChat: Chat = { assistant, messages: chatHistory };
      this.activeChats.next([...this.activeChats.value, newChat]);
      this.currentChat.next(newChat);
    } catch (error) {
      console.error('Error loading initial messages:', error);
    }
  }

  /**
   * Loads more messages for the current chat.
   */
  async loadMoreMessages(offset: number = 0, limit: number = 50): Promise<Message[]> {
    const focusedInsight = this.insightFocusService.getFocusedInsightModel();
    const focusedBusiness = this.focusedBusiness;

    if (!focusedInsight || !focusedBusiness) {
      console.error('Missing focused insight or business at load more');
      return [];
    }

    try {
      console.log(`Fetching messages with offset ${offset} and limit ${limit}`);
      const additionalMessages = await this.assistantService.getAssistantHistory(
        focusedInsight,
        focusedBusiness._id,
        limit
      );

      return additionalMessages.slice(offset, offset + limit);
    } catch (error) {
      console.error('Error loading more messages:', error);
      return [];
    }
  }

  /**
   * Removes the specified chat.
   */
  removeChat(chat: Chat): void {
    const updatedChats = this.activeChats.value.filter(activeChat => activeChat !== chat);
    this.activeChats.next(updatedChats);
    if (this.currentChat.value === chat && updatedChats.length > 0) {
      this.currentChat.next(updatedChats[0]);
    }
  }

  /**
   * Sets the waiting for rating state.
   */
  setWaitingForRating(waitingForRating: boolean): void {
    this.waitingForRating.next(waitingForRating);
  }

  /**
   * Sets the current chat.
   */
  setCurrentChat(chat: Chat): void {
    this.currentChat.next(chat);
  }

  /**
   * Sends a message to the assistant and sets a waiting flag.
   */
  sendMessageToAssistant(message: string): void {
    setTimeout(() => this.waitingForResponse.next(true), 500);
    this.webSocketService.emit(ClientEvents.send, { message });
  }

  /**
   * Adds a message to the current chat.
   */
  private addMessageToCurrentChat(message: Message): void {
    let parsedMessage = message;
    if (message.role === 'bot' && typeof message.message === 'string') {
      // First check if the message looks like JSON before trying to parse it
      const trimmedMessage = message.message.trim();
      if ((trimmedMessage.startsWith('{') && trimmedMessage.endsWith('}')) || 
          (trimmedMessage.startsWith('[') && trimmedMessage.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmedMessage);
          parsedMessage = {
            ...message,
            message: typeof parsed.message === 'string' ? parsed.message : message.message,
            choices: Array.isArray(parsed.choices) ? parsed.choices : message.choices
          };
        } catch (e) {
          // Not valid JSON despite appearances, leave as is
        }
      } else {
        // Try to extract JSON from the message if it contains JSON-like content
        try {
          const jsonRegex = /({[\s\S]*?})/;
          const match = message.message.match(jsonRegex);
          if (match && match[1] && 
              (match[1].includes('"message"') || match[1].includes('"choices"'))) {
            const parsed = JSON.parse(match[1]);
            parsedMessage = {
              ...message,
              message: typeof parsed.message === 'string' ? parsed.message : message.message,
              choices: Array.isArray(parsed.choices) ? parsed.choices : message.choices
            };
          }
        } catch {
          // Leave as is - not JSON or couldn't extract valid JSON
        }
      }
    }

    const updatedChats = this.activeChats.value.map(chat => {
      if (chat === this.currentChat.value) {
        return { ...chat, messages: [...chat.messages, parsedMessage] };
      }
      return chat;
    });

    this.activeChats.next(updatedChats);

    const updatedCurrentChat = updatedChats.find(chat => chat === this.currentChat.value);
    if (updatedCurrentChat) {
      this.currentChat.next(updatedCurrentChat);
    }
  }

  /**
   * Adds a message to the specified chat.
   */
  addMessage(chat: Chat, message: Message): void {
    // Parse assistant message if it's a JSON string
    let parsedMessage = message;
    if (message.role === 'bot' && typeof message.message === 'string') {
      // First check if the message looks like JSON before trying to parse it
      const trimmedMessage = message.message.trim();
      if ((trimmedMessage.startsWith('{') && trimmedMessage.endsWith('}')) || 
          (trimmedMessage.startsWith('[') && trimmedMessage.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmedMessage);
          parsedMessage = {
            ...message,
            message: typeof parsed.message === 'string' ? parsed.message : message.message,
            choices: Array.isArray(parsed.choices) ? parsed.choices : message.choices
          };
        } catch (e) {
          // Not valid JSON despite appearances, leave as is
        }
      } else {
        // Try to extract JSON from the message if it contains JSON-like content
        try {
          const jsonRegex = /({[\s\S]*?})/;
          const match = message.message.match(jsonRegex);
          if (match && match[1] && 
              (match[1].includes('"message"') || match[1].includes('"choices"'))) {
            const parsed = JSON.parse(match[1]);
            parsedMessage = {
              ...message,
              message: typeof parsed.message === 'string' ? parsed.message : message.message,
              choices: Array.isArray(parsed.choices) ? parsed.choices : message.choices
            };
          }
        } catch {
          // Not JSON or couldn't extract JSON, leave as is
        }
      }
    }
    const updatedChats = this.activeChats.value.map(activeChat => {
      if (activeChat === chat) {
        return { ...activeChat, messages: [...activeChat.messages, parsedMessage] };
      }
      return activeChat;
    });
    this.activeChats.next(updatedChats);
  }

  /**
   * Edits a message in the chat.
   */
  editMessage(event: { original: string; edited: string }): void {
    this.webSocketService.emit(ClientEvents.edit, event);
  }

  /**
   * Loads the last used chat.
   */
  async loadLastUsedChat(): Promise<void> {
    if (this.activeChats.value.length > 0) return;
    try {
      const assistantId = await this.assistantService.getLastUsedAssistant();
      await this.addChat(assistantId);
    } catch (error) {
      console.error('Error loading last used chat:', error);
    }
  }
}
